import {List, Map} from 'immutable';
import type {AuthoringGuideModelV1} from 'resources/GeneratedModels/AuthoringGuide/AuthoringGuideModel.v1';
import type {GuideModelV1} from 'resources/GeneratedModels/Guide/GuideModel.v1';
import type {AuthoringGuideLevelModelV2} from 'resources/GeneratedModels/AuthoringGuideLevel/AuthoringGuideLevelModel.v1';
import type {GuideLevelModelV2} from 'resources/GeneratedModels/GuideLevel/GuideLevelModel.v2';
import appStore from 'client/AppStore';

/**
 * The underlying generated guide models (`AuthoringGuideModelV1` / `GuideModelV1`)
 * are still untyped JS, so `this` inside these extension methods is dynamic. We
 * annotate `this` with the model union to restore the model context (`cache`,
 * `getMeta`, `getGuideLevels`, and the sibling extension methods called via `this`).
 */
type BaseGuideThis = AuthoringGuideModelV1 | GuideModelV1;

function getListFromStructure(
  guide: AuthoringGuideModelV1 | GuideModelV1,
  structure: Map<string, unknown>,
  oldList: List<unknown>
): List<Map<string, unknown>> {
  const newList = structure
    .reduce<List<unknown>>((prev = oldList, curr, key = '') => {
      const position = guide.getGuideLevelById(key).getPosition();
      // Immutable 3.8's `Map.isMap` is not a type guard; the runtime `isMap`
      // check guarantees this is a Map when the branch is taken.
      const child = structure.get(key) as Map<string, unknown>;
      if (Map.isMap(child) && !child.isEmpty()) {
        const children = getListFromStructure(guide, child, List());
        return prev.set(position, Map().set(key, children));
      }
      return prev.set(position, Map().set(key, List()));
    }, oldList)
    /**
     * Restoring this filter for a smoother QA experience since
     * faulty data will persist on dev unless explicitly fixed.
     * See PR for issue #671
     */
    .filterNot((val) => val === undefined) as List<Map<string, unknown>>;
  return newList;
}

function getLastLevelFromTheme(guideLevel: Map<string, List<unknown>>): string {
  const id = guideLevel.keySeq().first() as string;
  const children = guideLevel.first();
  if (children.isEmpty()) {
    return id;
  }
  return getLastLevelFromTheme(children.last() as Map<string, List<unknown>>);
}

export const baseGuideExtensions = {
  _getGuideLevelById(this: BaseGuideThis): AuthoringGuideLevelModelV2 | GuideLevelModelV2 {
    // Pass-through added for legacy model references
    return this.getGuideLevelById();
  },

  getLastGuideLevelsFromThemes(this: BaseGuideThis): List<string> {
    return this.cache(`getLastGuideLevelsFromThemes${this.getId()}`, () => {
      const lastLevels = this.getListFromStructure().reduce((result, theme) => {
        const id = getLastLevelFromTheme(theme);
        return result.push(id);
      }, List());

      return lastLevels;
    });
  },

  getListFromStructure(this: BaseGuideThis): List<Map<string, unknown>> {
    return this.cache('list_from_structure', () => {
      const structure = this.getMeta().getStructure();
      return getListFromStructure(this, structure, List());
    });
  },

  getGuideLevelById(this: BaseGuideThis, id: string): GuideLevelModelV2 {
    return this.cache(`getGuideLevelById${id}`, () =>
      this.getGuideLevels()
        .getCollection()
        .find((guideLevel) => guideLevel.getId() === id)
    );
  },

  getGuideLevelsWithAnswers(
    this: BaseGuideThis
  ): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    return this.cache('answered-guide-levels', () =>
      this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => {
          return guideLevel.getMetaStudentCountOfGuesses() > 0;
        })
    );
  },

  getMetaGuideLevelTree(this: BaseGuideThis): Map<string, unknown> {
    // Aliased for legacy model references
    return this.getMeta().getStructure();
  },

  getThemes(this: BaseGuideThis): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    return this.cache('themes', () => {
      return this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => guideLevel.isTheme());
    });
  },

  getTopics(
    this: BaseGuideThis,
    themeId?: string | null
  ): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    if (!themeId) {
      return this.cache('topics', () => {
        return this.getGuideLevels()
          .getCollection()
          .filter((guideLevel) => guideLevel.isTopic());
      });
    }

    return this.cache(`${themeId}-topics`, () => {
      const ids = this.getMetaGuideLevelTree().get(themeId, Map()).keySeq().toSet();

      return this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => {
          return ids.contains(guideLevel.getId());
        });
    });
  },

  getSubtopics(
    this: BaseGuideThis,
    themeId?: string | null,
    topicId?: string | null
  ): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    // TODO: can support only specifying one of themeId and topicId... but we probably never need that.
    if (!themeId && !topicId) {
      return this.cache('subtopics', () => {
        return this.getGuideLevels()
          .getCollection()
          .filter((guideLevel) => guideLevel.isSubtopic());
      });
    }

    return this.cache(`${themeId}-${topicId}-subtopics`, () => {
      const ids = this.getMetaGuideLevelTree().getIn([themeId, topicId], Map()).keySeq().toSet();

      return this.getGuideLevels()
        .getCollection()
        .filter((guideLevel) => {
          return ids.contains(guideLevel.getId());
        });
    });
  },

  getSortedThemes(this: BaseGuideThis): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    return this.cache('sorted-themes', () =>
      this.getThemes().sortBy((theme) => theme.getPosition())
    );
  },

  getSortedTopics(
    this: BaseGuideThis,
    themeId: string
  ): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    return this.cache(`${themeId}-sorted-topics`, () =>
      this.getTopics(themeId).sortBy((topic) => topic.getPosition())
    );
  },

  getSortedSubtopics(
    this: BaseGuideThis,
    themeId: string,
    topicId: string
  ): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    return this.cache(`${themeId}-${topicId}-sorted-subtopics`, () => {
      return this.getSubtopics(themeId, topicId).sortBy((subtopic) => subtopic.getPosition());
    });
  },

  isTopicFree(this: BaseGuideThis, themeId: string, topicId: string): boolean {
    return this.cache(`${topicId}-is-free`, () => {
      const topic = this.getGuideLevelById(topicId);
      if (topic === undefined) {
        return false;
      }

      return (
        topic.isFree() && this.getSubtopics(themeId, topicId).every((subtopic) => subtopic.isFree())
      );
    });
  },

  getSortedGuideLevels(
    this: BaseGuideThis
  ): List<GuideLevelModelV2> | List<AuthoringGuideLevelModelV2> {
    // `routerProps` is dynamic route props (typed `Record<string, unknown>`),
    // so narrow the location accessor at the use site.
    const location = appStore.routerProps.location as {search: string};
    return this.cache(`sortedGuideLevels${location.search}`, () => {
      let sortedCollection = List();
      this.getSortedThemes().forEach((theme) => {
        sortedCollection = sortedCollection.push(theme);
        this.getSortedTopics(theme.getId()).forEach((topic) => {
          sortedCollection = sortedCollection.push(topic);
          this.getSortedSubtopics(theme.getId(), topic.getId()).forEach((subtopic) => {
            sortedCollection = sortedCollection.push(subtopic);
          });
        });
      });
      return sortedCollection;
    });
  },

  getSortedFRQItems(this: BaseGuideThis, usePublic?: boolean): List<unknown> {
    return this.cache(`sortedFRQGuideLevels${this.getId()}`, () => {
      let sortedCollection = List();
      this.getSortedThemes().forEach((theme) => {
        sortedCollection = sortedCollection.push(theme);
        this.getSortedTopics(theme.getId()).forEach((topic) => {
          sortedCollection = sortedCollection.push(topic);
          // sorting question sets by position which is only accessible via the `relationshipMeta` object
          const qSets = topic
            .getQuestionSets()
            .sortBy((set) =>
              set.relationshipMeta?.get('guide_level')?.get(topic.getId())?.get('position')
            );
          qSets.forEach((set) => {
            // Immutable 3.8's `concat` is typed as returning the wider `Iterable`,
            // but at runtime returns a `List`.
            sortedCollection = sortedCollection.concat(
              usePublic ? set.getPublicQuestions() : set.getQuestions()
            ) as List<unknown>;
          });
          this.getSortedSubtopics(theme.getId(), topic.getId()).forEach((subtopic) => {
            sortedCollection = sortedCollection.push(subtopic);
            subtopic.getQuestionSets().forEach((set) => {
              sortedCollection = sortedCollection.concat(
                usePublic ? set.getPublicQuestions() : set.getQuestions()
              ) as List<unknown>;
            });
          });
        });
      });
      return sortedCollection;
    });
  }
};
